/**
 * A one-at-a-time command menu.
 *
 * Built as a singleton rather than one popover per row. A layers panel
 * re-renders its whole list on every change, so per-row menus would attach and
 * discard document listeners on every stroke — and the row a menu belongs to
 * can vanish underneath it mid-interaction.
 *
 * Unlike the colour picker, this **takes focus on open**. A menu is a transient
 * list of commands: arrive, choose, leave. A picker is a surface you work in
 * alongside the canvas, so stealing focus there would be wrong. Same primitive
 * family, opposite answer, for a reason worth writing down.
 *
 * It also carries the accessible half of drag-to-reorder. WCAG 2.2 SC 2.5.7
 * requires a single-pointer path for anything achievable by dragging, and
 * "Move up" in here is that path — as well as being faster than dragging on a
 * phone regardless.
 */

export interface MenuItem {
  label: string;
  onSelect: () => void;
  /** Disabled items stay visible and say why. Hiding them moves the others. */
  disabled?: boolean;
  /** Shown when disabled, so a greyed row is never a mystery. */
  reason?: string;
  /** Destructive items sit last and read differently. */
  danger?: boolean;
}

let panel: HTMLElement | null = null;
let openAnchor: HTMLElement | null = null;

function ensurePanel(): HTMLElement {
  if (panel) return panel;
  const el = document.createElement("div");
  el.className = "menu popover floating";
  el.setAttribute("role", "menu");
  document.body.appendChild(el);

  document.addEventListener("pointerdown", (e) => {
    if (!openAnchor) return;
    const t = e.target as Node | null;
    if (!t || el.contains(t) || openAnchor.contains(t)) return;
    closeMenu();
  });

  document.addEventListener("keydown", (e) => {
    if (!openAnchor) return;
    const buttons = [...el.querySelectorAll<HTMLButtonElement>("button:not([disabled])")];
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);

    if (e.key === "Escape") {
      e.preventDefault();
      const anchor = openAnchor;
      closeMenu();
      anchor.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      buttons[(at + 1) % buttons.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      buttons[(at - 1 + buttons.length) % buttons.length]?.focus();
    } else if (e.key === "Tab") {
      // Tabbing out of a transient menu should dismiss it, not move through it.
      closeMenu();
    }
  });

  panel = el;
  return el;
}

export function closeMenu(): void {
  if (!panel || !openAnchor) return;
  panel.removeAttribute("data-open");
  openAnchor.setAttribute("aria-expanded", "false");
  openAnchor = null;
}

export function showMenu(anchor: HTMLElement, items: MenuItem[]): void {
  const el = ensurePanel();

  // Clicking the same trigger twice closes, like every other menu.
  if (openAnchor === anchor) {
    closeMenu();
    return;
  }
  closeMenu();

  el.innerHTML = "";
  for (const item of items) {
    const b = document.createElement("button");
    b.type = "button";
    b.setAttribute("role", "menuitem");
    b.className = "menu-item";
    if (item.danger) b.setAttribute("data-danger", "");
    b.disabled = Boolean(item.disabled);
    if (item.disabled && item.reason) b.title = item.reason;

    const label = document.createElement("span");
    label.textContent = item.label;
    b.appendChild(label);

    if (item.disabled && item.reason) {
      const why = document.createElement("span");
      why.className = "menu-why";
      why.textContent = item.reason;
      b.appendChild(why);
    }

    b.addEventListener("click", () => {
      // Close first: the handler usually re-renders the list this menu hangs
      // off, and closing afterwards would be closing against a dead anchor.
      closeMenu();
      item.onSelect();
    });
    el.appendChild(b);
  }

  openAnchor = anchor;
  anchor.setAttribute("aria-expanded", "true");
  el.setAttribute("data-open", "");

  // Measured after display is on — a display:none element has no box.
  const a = anchor.getBoundingClientRect();
  const own = el.getBoundingClientRect();
  const left = Math.min(a.right - own.width, window.innerWidth - own.width - 8);
  // Flip above the trigger when there is no room below, which for a row near
  // the bottom of a 200px panel is most of the time.
  const below = a.bottom + 4;
  const top = below + own.height > window.innerHeight - 8 ? a.top - own.height - 4 : below;
  el.style.left = `${Math.max(8, left)}px`;
  el.style.top = `${Math.max(8, top)}px`;

  el.querySelector<HTMLButtonElement>("button:not([disabled])")?.focus();
}
